import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { IStoreState } from '../../store/IStoreState';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { formatBytes } from '../../utils/formatBytes';
import {
    PanelBody,
    PanelHeading,
    PanelWithoutForm,
    Table,
    TableBody,
    TableData,
    TableEmphasizedText,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSubText,
} from '../elements';
import { FormatCondition } from '../Objects/FormatCondition';
import { Spinner } from '../spinners';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { SurveyStatusLabel } from '../utils/SurveyStatusLabel';
import { TagLabels } from '../utils/TagLabels';

interface ISurvey {
    id: string;
    name: string;
    description: string;
    location: string;
    surveyId?: string;
    surveryedOn?: Date;
    condition?: string;
    careCondition?: string;
    elementsCount: number;
    surveysCount: number;
    storageUsed: number;
    status: string;
    objectCompositionIsVisible: boolean;
    objectIsPublic: boolean;
    craInitialHistoryCondition?: number;
    craInspectionCondition?: number;
    inspectionStandardType: InspectionStandardTypes;
    labels: string[];
}

function SurveysTable(props: { items: ISurvey[]; useProductTitle?: boolean | undefined }) {
    const translate = useTranslate();
    const navigate = useNavigate();

    const areLabelsVisibleInOverviews = useSelector((state: IStoreState) => state.authentication.areLabelsVisibleInOverviews);

    return (
        <PanelBody noPadding={true}>
            <Table hover={true}>
                <TableHead>
                    <TableHeadRow light={true}>
                        <TableHeader>{props.useProductTitle ? translate('common.name') : translate('common.survey')}</TableHeader>
                        <TableHeader>{props.useProductTitle ? translate('common.template') : translate('common.inspectionTemplate')} </TableHeader>
                        <TableHeader>{translate('survey.size')}</TableHeader>
                        <TableHeader>{translate('survey.surveyedOn')}</TableHeader>
                        <TableHeader>{translate('common.result')}</TableHeader>
                        <TableHeader align="right">{translate('common.status')}</TableHeader>
                    </TableHeadRow>
                </TableHead>

                <TableBody>
                    {props.items.map((item) => (
                        <TableRow key={item.id} onClick={() => navigate(`/surveys/${item.id}`)}>
                            <TableData>
                                <TableEmphasizedText text={item.name} />
                                <TableSubText text={item.description} />
                                {areLabelsVisibleInOverviews && <TagLabels labels={item.labels} />}
                            </TableData>

                            <TableData>
                                {(item.inspectionStandardType && <>{translate(`inspectionStandard.${item.inspectionStandardType}`)} </>) || (
                                    <>NEN 2767</>
                                )}
                            </TableData>

                            <TableData nowrap={true}>{formatBytes(item.storageUsed)}</TableData>
                            <TableData nowrap={true}>
                                <NullableFormattedDate value={item.surveryedOn} />
                            </TableData>

                            <TableData>
                                <FormatCondition
                                    condition={item.condition}
                                    careCondition={item.careCondition}
                                    inspectionStandardType={item.inspectionStandardType}
                                />
                            </TableData>
                            <TableData alignRight={true}>
                                <SurveyStatusLabel value={item.status} />
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </PanelBody>
    );
}

export function DashboardSurveysList(props: { daysVisibilityFutureSurveys: number; useProductTitle?: boolean | undefined }) {
    const [recentSurveys, setRecentSurveys] = React.useState<ISurvey[] | null>(null);
    const [futureSurveys, setFutureSurveys] = React.useState<ISurvey[] | null>(null);

    const translate = useTranslate();

    useEffect(() => {
        async function loadSurveys() {
            const recentSurveysResponse = await fetchGet('/api/surveys', {
                limit: 50,
                surveryedOn: { lessThanOrEqual: new Date().toISOString() },
                sortColumns: [{ columnName: 'surveryedOn', direction: 'descending' }],
            });
            setRecentSurveys(recentSurveysResponse.json.items);

            const futureDate = new Date();
            futureDate.setDate(futureDate.getDate() + props.daysVisibilityFutureSurveys);
            const futureSurveysResponse = await fetchGet('/api/surveys', {
                limit: 20,
                surveryedOn: { greaterThanOrEqual: new Date().toISOString(), lessThanOrEqual: futureDate.toISOString() },
                sortColumns: [{ columnName: 'surveryedOn', direction: 'ascending' }],
            });
            setFutureSurveys(futureSurveysResponse.json.items);
        }
        void loadSurveys();
    }, [props.daysVisibilityFutureSurveys]);

    return (
        <>
            {futureSurveys && futureSurveys.length > 0 && (
                <>
                    <PanelWithoutForm>
                        <PanelHeading
                            icon="fa fa-camera"
                            text={
                                props.useProductTitle
                                    ? translate('dashboard.futureProductList', props.daysVisibilityFutureSurveys.toString())
                                    : translate('dashboard.futureSurveyList', props.daysVisibilityFutureSurveys.toString())
                            }
                        />
                        <SurveysTable items={futureSurveys} useProductTitle={props.useProductTitle} />
                    </PanelWithoutForm>
                </>
            )}
            <PanelWithoutForm>
                <PanelHeading
                    icon="fa fa-camera"
                    text={props.useProductTitle ? translate('dashboard.recentProductList') : translate('dashboard.surveyList')}
                />

                {(() => {
                    if (!recentSurveys) {
                        return <Spinner />;
                    }
                    if (recentSurveys.length === 0) {
                        return (
                            <PanelBody>
                                <h5>{translate('dashboard.noSurvey')} </h5>
                            </PanelBody>
                        );
                    }

                    return <SurveysTable items={recentSurveys} useProductTitle={props.useProductTitle} />;
                })()}
            </PanelWithoutForm>
        </>
    );
}
